import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';
import { LineChart } from 'react-native-chart-kit';
import { Dimensions } from 'react-native';

const screenWidth = Dimensions.get('window').width;

interface EnergyMeterCardProps {
  deviceId: string;
  deviceName: string;
  lastData?: any;
  historicalData?: any[];
  config?: any;
}

export const EnergyMeterCard: React.FC<EnergyMeterCardProps> = ({
  deviceName,
  lastData,
  historicalData,
  config,
}) => {
  const { t } = useTranslation();

  const energyKwh = lastData?.energy_kwh || 0;
  const powerW = lastData?.power_w || 0;
  const voltageV = lastData?.voltage_v || 0;
  const currentA = lastData?.current_a || 0;

  // Prepare chart data
  const chartData = historicalData?.slice(-10).map((d) => d.power_w || 0) || [0];

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <Ionicons name="flash" size={24} color="#14b8a6" />
        <Text style={styles.deviceName}>{deviceName}</Text>
      </View>

      {/* Main Energy Display */}
      <View style={styles.energyDisplay}>
        <Text style={styles.energyValue}>{energyKwh.toFixed(2)}</Text>
        <Text style={styles.energyUnit}>kWh</Text>
      </View>

      {/* Power Metrics Grid */}
      <View style={styles.metricsGrid}>
        <View style={styles.metricBox}>
          <Ionicons name="flash-outline" size={20} color="#f97316" />
          <Text style={styles.metricValue}>{powerW.toFixed(0)} W</Text>
          <Text style={styles.metricLabel}>{t('deviceDetail.power')}</Text>
        </View>

        <View style={styles.metricBox}>
          <Ionicons name="speedometer-outline" size={20} color="#3b82f6" />
          <Text style={styles.metricValue}>{voltageV.toFixed(1)} V</Text>
          <Text style={styles.metricLabel}>{t('deviceDetail.voltage')}</Text>
        </View>

        <View style={styles.metricBox}>
          <Ionicons name="git-network-outline" size={20} color="#8b5cf6" />
          <Text style={styles.metricValue}>{currentA.toFixed(2)} A</Text>
          <Text style={styles.metricLabel}>{t('deviceDetail.current')}</Text>
        </View>
      </View>

      {/* Power Chart */}
      {chartData.length > 1 && (
        <View style={styles.chartContainer}>
          <Text style={styles.chartTitle}>{t('deviceDetail.powerTrend')}</Text>
          <LineChart
            data={{
              labels: [],
              datasets: [{ data: chartData }],
            }}
            width={screenWidth - 64}
            height={150}
            chartConfig={{
              backgroundColor: '#14b8a6',
              backgroundGradientFrom: '#14b8a6',
              backgroundGradientTo: '#0d9488',
              decimalPlaces: 0,
              color: (opacity = 1) => `rgba(255, 255, 255, ${opacity})`,
              style: { borderRadius: 16 },
            }}
            bezier
            withDots={false}
            style={styles.chart}
          />
        </View>
      )}

      {/* Last Update */}
      {lastData?.timestamp && (
        <Text style={styles.timestamp}>
          {t('deviceDetail.lastUpdate')}: {new Date(lastData.timestamp).toLocaleString()}
        </Text>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  },
  deviceName: {
    fontSize: 18,
    fontWeight: '600',
    marginLeft: 12,
    color: '#1f2937',
  },
  energyDisplay: {
    alignItems: 'center',
    paddingVertical: 24,
    backgroundColor: '#f0fdfa',
    borderRadius: 12,
    marginBottom: 16,
  },
  energyValue: {
    fontSize: 48,
    fontWeight: '700',
    color: '#14b8a6',
  },
  energyUnit: {
    fontSize: 16,
    color: '#6b7280',
    marginTop: 4,
  },
  metricsGrid: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  metricBox: {
    flex: 1,
    alignItems: 'center',
    padding: 12,
    backgroundColor: '#f9fafb',
    borderRadius: 8,
    marginHorizontal: 4,
  },
  metricValue: {
    fontSize: 18,
    fontWeight: '600',
    color: '#1f2937',
    marginTop: 8,
  },
  metricLabel: {
    fontSize: 12,
    color: '#6b7280',
    marginTop: 4,
  },
  chartContainer: {
    marginTop: 16,
  },
  chartTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#374151',
    marginBottom: 8,
  },
  chart: {
    borderRadius: 12,
  },
  timestamp: {
    fontSize: 12,
    color: '#9ca3af',
    textAlign: 'center',
    marginTop: 12,
  },
});
